import {
  Alert,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import moment, { now } from "moment";
import { LoadingButton } from "@mui/lab";
import { MuiTelInput, matchIsValidTel } from "mui-tel-input";
import PostHooks from "../../../Api/PostHooks";
import { Link, useNavigate } from "react-router-dom";
import AlertComponent from "../../../Components/AlertComponent";
import { Group } from "@mui/icons-material";
import ResidentialDropdowns from "../../../Components/ResidentialDropdowns";
import FormNumberInput from "../../../Components/FormNumberInput";

const CustomerRegistrationForm = () => {
  const methods = useForm();
  const {
    watch,
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = methods;
  const navigate = useNavigate();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const msisdn = watch("msisdn");

  const [loading, response, request] = PostHooks();

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);

  const processSubmission = async (e) => {
    try {
      let formData = {
        first_name: e.first_name,
        surname: e.surname,
        other_names: e.other_names,
        company_name: "",
        state: "",
        residential_address:
          e.county + " " + e.subcounty + " " + e.ward + " " + e.area,
        postal_address: "",
        id_number: e.identification_number,
        passport_number: e.identification_number,
        county: e.county,
        subcounty: e.subcounty,
        ward: e.ward,
        village: e.area,
        serial_no: "",
        msisdn: msisdn.replace("+", ""),
        document_registration_date: moment(now).format("YYYY-MM-DD"),
        document_expiry_date: moment(now).format("YYYY-MM-DD"),
        name: "National ID",
        document_number: e.identification_number,
        description: "Customer registration",
        registry_file_number: "",
        role: "customer",
        channel_name: "mobile_number",
        username: e.identification_number,
      };

      let props = {
        url: "/user",
        body: formData,
      };

      const regResp = await request(props);

      if (regResp && regResp.status === 200 && regResp.data.StatusCode === 0) {
        setTimeout(() => {
          navigate("/login");
        }, 1000);

        setSnackbar({
          open: true,
          severity: "success",
          message: "Registration successful",
        });
      } else {
        const errorMessage =
          regResp?.data?.StatusMessage ??
          regResp?.data?.message ??
          "An unexpected error occurred. Please try again.";

        setSnackbar({
          open: true,
          severity: "error",
          message: errorMessage,
        });
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.StatusMessage ??
        error.message ??
        "An unexpected error occurred. Please try again.";

      setSnackbar({
        open: true,
        severity: "error",
        message: errorMessage,
      });
    }
  };

  const onSubmit = (e) => {
    if (msisdn && msisdn.length < 13) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Input a valid phone number!",
      });
    } else {
      setPendingFormData(e);
      setOpenConfirmDialog(true);
    }
  };

  const handleConfirmSubmit = () => {
    setOpenConfirmDialog(false);
    if (pendingFormData) {
      processSubmission(pendingFormData);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} id="cust_reg_form">
        <AlertComponent
          open={snackbar.open}
          handleSnackbar={handleSnackbarClose}
          severity={{ severity: snackbar.severity, message: snackbar.message }}
        />

        <Grid container rowSpacing={3} columnSpacing={{ xs: 1, sm: 2 }}>
          <Grid
            item
            xs={6}
            md={4}
            lg={4}
            sx={{
              paddingTop: "20px",
            }}
          >
            <FormControl fullWidth error={!!errors.first_name}>
              <InputLabel htmlFor="first_name">First name</InputLabel>
              <OutlinedInput
                id="first_name"
                {...register("first_name", { required: true })}
                label="First name"
                xs={12}
                variant="outlined"
              />
              {errors.first_name && <FormHelperText>Required</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid
            item
            xs={6}
            md={4}
            lg={4}
            sx={{
              paddingTop: "20px",
            }}
          >
            <FormControl fullWidth error={!!errors.surname}>
              <InputLabel htmlFor="surname">Surname</InputLabel>
              <OutlinedInput
                id="surname"
                {...register("surname", { required: true })}
                label="Surname"
                xs={12}
                variant="outlined"
              />
              {errors.surname && <FormHelperText>Required</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid
            item
            xs={12}
            md={4}
            lg={4}
            sx={{
              paddingTop: "20px",
            }}
          >
            <FormControl fullWidth error={!!errors.other_names}>
              <InputLabel htmlFor="other_names">Other name</InputLabel>
              <OutlinedInput
                variant="outlined"
                id="other_names"
                {...register("other_names", { required: true })}
                label="Other name"
                xs={12}
              />
              {errors.other_names && <FormHelperText>Required</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
            lg={6}
            sx={{
              paddingTop: "20px",
            }}
          >
            <FormNumberInput
              name="identification_number"
              control={control}
              label={`National ID Number/ Passport Number`}
              min={7}
              max={10}
              rules={{
                required: "Enter a valid id number.",
              }}
            />

            {/* <FormControl fullWidth error={!!errors.identification_number}>
              <InputLabel>National ID Number/ Passport Number</InputLabel>
              <OutlinedInput
                variant="filled"
                id="identification_number"
                {...register("identification_number", {
                  required: true,
                  maxLength: 10,
                  minLength: 7,
                })}
                label="National ID Number/ Passport Number"
                xs={12}
                type="number"
              />
              {errors.identification_number && (
                <FormHelperText>Enter a valid id number.</FormHelperText>
              )}
            </FormControl> */}
          </Grid>

          {/* 
        <Grid
          item
          xs={12}
          md={12}
          lg={12}
          sx={{
            paddingTop: "20px",
          }}
        >
          <FormControl fullWidth error={!!errors.username}>
            <InputLabel htmlFor="username">Username</InputLabel>
            <OutlinedInput
              variant="filled"
              id="username"
              {...register("username", { required: true })}
              label="Username"
              xs={12}
            />
            {errors.username && <FormHelperText>Required</FormHelperText>}
          </FormControl>
        </Grid> */}

          <Grid
            item
            xs={12}
            md={6}
            lg={6}
            sx={{
              paddingTop: "20px",
            }}
          >
            <Controller
              name="msisdn"
              control={control}
              rules={{
                validate: (value) => matchIsValidTel(value),
              }}
              defaultValue={"+254"}
              render={({ field, fieldState }) => (
                <MuiTelInput
                  {...field}
                  fullWidth
                  defaultCountry="KE"
                  onlyCountries={["KE"]}
                  helperText={
                    fieldState.invalid ? "Invalid mobile number!" : ""
                  }
                  error={fieldState.error}
                  value={field.value || ""}
                  disableFormatting
                />
              )}
            />
          </Grid>

          <ResidentialDropdowns />

          {/* <Grid
          item
          xs={12}
          md={6}
          lg={6}
         
          sx={{
            paddingTop: "20px",
          }}
        >
          <FormControl fullWidth>
            <InputLabel id="user_role">User role</InputLabel>
            <Select
              labelId="user_role"
              id="user_role"
              defaultValue=""
              value={userRole}
              label="Identification type"
              onChange={(e) => setUserRole(e.target.value)}
            >
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="finance">Finance</MenuItem>
              <MenuItem value="shareholder">Shareholder</MenuItem>
              <MenuItem value="dealer">Dealer</MenuItem>
              <MenuItem value="supplier">Supplier</MenuItem>
            </Select>
          </FormControl>
        </Grid> */}

          {/* <Grid
          item
          xs={12}
          md={6}
          lg={6}
         
          sx={{
            paddingTop: "20px",
          }}
        >
          <FormControl fullWidth>
            <InputLabel id="document_type">Identification type</InputLabel>
            <Select
              labelId="document_type"
              id="document_type"
              defaultValue=""
              value={documentType}
              label="Identification type"
              onChange={(e) => setDocumenType(e.target.value)}
            >
              <MenuItem value="National ID">National Id</MenuItem>
              <MenuItem value="Passpot ID">Passport</MenuItem>
              <MenuItem value="Company Certificate">
                Company certificate
              </MenuItem>
              <MenuItem value="KRA PIN">KRA Pin</MenuItem>
            </Select>
          </FormControl>
        </Grid> */}

          <Grid
            item
            xs={12}
            sx={{
              py: "10px",
              display: "flex",
              gap: "20px",
              justifyContent: "space-between",
            }}
          >
            {/* <Button
            variant="outlined"
            size="large"
            type="reset"
            onClick={() => navigate(-1)}
            fullWidth
          >
            Login
          </Button> */}
            <LoadingButton
              variant="contained"
              // sx={{ ml: "10px" }}
              size="large"
              type="submit"
              loading={loading}
              fullWidth
            >
              register
            </LoadingButton>
          </Grid>

          <Grid
            item
            xs={12}
            sx={{
              py: "5px",
              display: "flex",
              // gap: "20px",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="body2" align="">
              Already have an account?{" "}
              <Link variant="subtitle2" component={""} to={"/login"}>
                Login
              </Link>
            </Typography>
          </Grid>
        </Grid>
      </form>

      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Confirm Registration Details"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Please verify that the details you entered are correct.
          </DialogContentText>
          <Stack spacing={1} sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Name:</strong> {pendingFormData?.first_name}{" "}
              {pendingFormData?.surname} {pendingFormData?.other_names}
            </Typography>
            <Typography variant="body2">
              <strong>ID Number:</strong>{" "}
              {pendingFormData?.identification_number}
            </Typography>
            <Typography variant="body2">
              <strong>Phone Number:</strong> {msisdn}
            </Typography>
            <Typography variant="body2">
              <strong>Location:</strong> {pendingFormData?.county},{" "}
              {pendingFormData?.subcounty}, {pendingFormData?.ward},{" "}
              {pendingFormData?.area}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ m: 2 }}>
          <Button
            onClick={() => setOpenConfirmDialog(false)}
            variant="outlined"
          >
            Edit
          </Button>
          <Button onClick={handleConfirmSubmit} autoFocus variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

export default CustomerRegistrationForm;
